<template>
  <footer
    class="row"
    role="contentinfo"
  >
    <parent-reference-line-item-id class="ma-0 body-2" />
    <ul
      class="links-container"
      role="link"
    >
      <li
        v-if="!hasHideFooterLinksEnabled"
        role="link"
      >
        <a
          id="faqUrl"
          :href="faqUrl"
          target="_blank"
          @click="faqClicked"
        >
          {{ $t('footer-faq') }}
        </a>
      </li>
      <li
        v-if="!hasHideFooterLinksEnabled"
        role="link"
      >
        <a
          id="knowledgeBaseUrl"
          :href="knowledgeBaseUrl"
          target="_blank"
          @click="helpCenterClicked"
        >
          {{ $t('help-center') }}
        </a>
      </li>
      <li
        v-if="termsOfServiceSplitEnabled"
        role="link"
      >
        <a
          id="termsOfServiceLink"
          :href="termsOfServiceLink"
          target="_blank"
        >
          {{ $t('footer-tos') }}
        </a>
      </li>
      <li
        v-else
        role="link"
      >
        <router-link :to="formatRouterTo({ name: 'TermsOfService' })">
          {{ $t('footer-tos') }}
        </router-link>
      </li>
      <li
        role="link"
      >
        <a
          id="privacyUrl"
          :href="privacyUrl"
          target="_blank"
        >
          {{ $t('footer-privacy') }}
        </a>
      </li>
      <li
        role="link"
      >
        <a
          id="licencesUrl"
          :href="licencesUrl"
          target="_blank"
        >
          {{ $t('footer-licenses') }}
        </a>
      </li>
      <li
        v-if="hasCombinableRewardLinksEnabled"
        role="link"
      >
        <router-link :to="accountRoute">
          {{ $t('wallet.title') }}
        </router-link>
      </li>
    </ul>
    <aside
      id="footer-logo"
      class="text-center"
    >
      <promo-link-logo v-if="isPromoLink" />
      <reward-link-logo v-if="isRewardLink || isDisbursementLink" />
    </aside>
    <aside class="copyright">
      &copy; {{ currentYear }} Tango Card Inc.
    </aside>
  </footer>
</template>

<script>
import { mapGetters } from 'vuex'
import { TYPE_OF_REWARD_LINK } from '@/store/get-types'
import PromoLinkLogo from '@/components/footer/PromoLinkLogo'
import RewardLinkLogo from '@/components/footer/RewardLinkLogo'
import {
  adjustRouteNameBasedOnKey,
  getExistingRouteNameEquivalent,
  getNonExistingRouteNameEquivalent
} from '@/router/routes/utilities'
import {
  SVP_RL_HIDE_FOOTER_LINKS,
  SVP_COMBINABLE_RLS, RX_DISB_LINK
} from '@/plugins/split/ActiveSplits'
import { WALLET_ROUTE } from '@/router/routes/route-names'
import ParentReferenceLineItemId from '@/components/shared/ParentReferenceLineItemId'
import { HELP_CENTER_CLICKED, FAQ_CLICKED } from '@/plugins/segment/SegmentConstants'
import { RewardLinkType } from '@/store/constants/reward-link-constants'

export default {
  name: 'Footer',
  components: {
    ParentReferenceLineItemId,
    PromoLinkLogo,
    RewardLinkLogo
  },
  props: {
    existingRewardLinkExists: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      rewardLinkType: TYPE_OF_REWARD_LINK
    }),
    currentYear () {
      return new Date().getFullYear()
    },
    knowledgeBaseUrl () {
      return process.env.VUE_APP_KNOWLEDGE_BASE_URL
    },
    faqUrl () {
      return `${this.knowledgeBaseUrl}/s/topic/0TO1U000000ALkvWAG/faqs-all`
    },
    privacyUrl () {
      return `${process.env.VUE_APP_TANGO_CARD_WEBSITE_URL}/privacy-notice/`
    },
    licencesUrl () {
      return `${process.env.VUE_APP_TANGO_CARD_WEBSITE_URL}/licenses/`
    },
    hasHideFooterLinksEnabled () {
      return this.$isSplitEnabled(SVP_RL_HIDE_FOOTER_LINKS)
    },
    hasCombinableRewardLinksEnabled () {
      return this.$isSplitEnabled(SVP_COMBINABLE_RLS)
    },
    accountRoute () {
      return { name: WALLET_ROUTE }
    },
    isRewardLink () {
      return RewardLinkType.REWARD_LINK === this.rewardLinkType
    },
    isPromoLink () {
      return RewardLinkType.PROMO_LINK === this.rewardLinkType
    },
    isDisbursementLink () {
      return RewardLinkType.DISBURSEMENT_LINK === this.rewardLinkType
    },
    termsOfServiceSplitEnabled () {
      return this.$isSplitEnabled(RX_DISB_LINK)
    },
    termsOfServiceLink () {
      let anchor = '#rewardlink'
      if (this.isDisbursementLink) {
        anchor = '#disbursementlink'
      }
      return `${process.env.VUE_APP_TANGO_CARD_WEBSITE_URL}/terms-of-service/${anchor}`
    }
  },
  methods: {
    formatRouterTo (route) {
      const keyRetrival = this.existingRewardLinkExists
        ? getExistingRouteNameEquivalent
        : getNonExistingRouteNameEquivalent
      return { name: adjustRouteNameBasedOnKey(route, keyRetrival) }
    },
    faqClicked () {
      this.$segment.track(FAQ_CLICKED)
    },
    helpCenterClicked () {
      this.$segment.track(HELP_CENTER_CLICKED)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/css/variables';
@import '../../scss/variables';

#footer {
  display: flex;
  flex-direction: column;
  padding: 32px 5vw;
  background-color: $tangoDkBlue;
  color: $tangoWhite;

  ul.links-container {
    list-style-type: none;
    margin: 25px 0;
    font-size: 16px;
    padding: 0;

    a,
    a:hover,
    a:visited,
    a:active,
    a:link {
      color: $tangoWhite;
      text-decoration: none;
    }

    li {
      color: $tangoWhite;
      text-decoration: underline;
      cursor: pointer;
      display: inline-block;

      &:not(:first-of-type) :before {
        display: inline-block;
        content: ' | ';
        width: 40px;
      }
    }
  }

  #footer-logo {
    margin-bottom: 14px;
    margin-top: 24px;
  }

  .copyright {
    font-size: 12px;
  }
}

// pipes are too wide, time to break
@media screen and (max-width: $breakpoint-md - 1) {
  ul.links-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-bottom: 0;

    li {
      text-align: left;
      width: 35vw;
      height: 38px;
      line-height: 38px;

      // hide the pipes on smaller screens
      :before {
        display: none !important;
      }
    }
  }
}

// when it gets even smaller, change some spacing
@media screen and (max-width: $breakpoint-xs - 1) {
  #footer {
    padding: 32px 0;

    ul.links-container li {
      width: 50%;
      padding-left: 8vw;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
